/* Copyright (c) 2019-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { Commands } from 'bridge-commands/commands';
import { Types as CommandTypes } from 'bridge-commands/types';
import { BrowserPlugin } from 'crimson/lib/bridge/browser-plugin/browser-plugin';
import { PluginResponse, PluginHandlers } from 'crimson/lib/bridge/common/plugin.models';
import { DbService } from 'crimson/lib/db/db.service';
import { FullBridgeCommand } from 'crimson/lib/bridge/bridge.service';
import { BrowserDbNames } from '../models';

import Types = CommandTypes.Common.Applicationcontrol;

const Applicationcontrol = Commands.Common.Applicationcontrol;

@Injectable({
    providedIn: 'root'
})
export class ApplicationControlPlugin extends BrowserPlugin {
    constructor(private dbService: DbService) {
        super();
    }

    public get handlers(): PluginHandlers {
        return {
            module: Applicationcontrol.DeleteCookies.module,
            commands: {
                [Applicationcontrol.DeleteCookies.method]: this.deleteCookies.bind(this),
                [Applicationcontrol.LoadStringTable.method]: this.loadStringTable.bind(this),
                [Applicationcontrol.ReadConfig.method]: this.readConfig.bind(this),
                [Applicationcontrol.WriteConfig.method]: this.writeConfig.bind(this)
            }
        };
    }

    private deleteCookies(command: FullBridgeCommand<Types.DeleteCookiesParameters>): Observable<PluginResponse> {
        // Not possible to delete cookies when loaded in browser, hence mocking to always return true.
        // When application is loaded in browser, the cookie instances shall be shared.
        // for e.g. facebook, google login
        return this.makeResult('true');
    }

    private loadStringTable(command: FullBridgeCommand<Types.LoadStringTableParameters>): Observable<PluginResponse> {
        // This endpoint is required only for cef appliations for showing error page.
        // Not required in browser.
        return this.makeResult('true');
    }

    private readConfig(command: FullBridgeCommand<Types.ReadConfigParameters>): Observable<PluginResponse> {
        return this.dbService
            .getGlobalStore(BrowserDbNames.Config)
            .getItem(command.payload.appname)
            .pipe(
                catchError(err => this.makeError(500, 'IndexedDB Error: ' + err.toString())),
                mergeMap(response => this.makeResult(response))
            );
    }

    private writeConfig(command: FullBridgeCommand<Types.WriteConfigParameters>): Observable<PluginResponse> {
        return this.dbService
            .getGlobalStore(BrowserDbNames.Config)
            .setItem(command.payload.appname, command.payload.data)
            .pipe(
                catchError(err => this.makeError(500, 'IndexedDB Error: ' + err.toString())),
                mergeMap(response => this.makeResult('true'))
            );
    }
}
